import React, { useEffect, useState } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import {
  ArrowLeft, Mail, Phone, IdCard, CalendarDays, Plus,
  Save, NotebookPen, FileText, Pill, Files, Clock, Trash2, ChevronRight,
  Stethoscope, HeartPulse, Brain, LifeBuoy, History, Activity,
  Bone, Smile, HelpCircle, Users, Sparkles
} from "lucide-react";
import {
  getPatient, getAnamnese, upsertAnamnese, listPatientSessions, listAnamneses,
  createSession, updateSession, deleteSession, updatePatientProfile, setLinkStatus,
  getPatientBalance, addPatientCredits, listPlans,
  getProfessionalProfile, listAnamneseTemplates, getProfessionalUsesCredits
} from "@/lib/db";
import Avatar from "@/components/common/Avatar";
import { PatientStatusBadge } from "@/components/common/StatusBadge";
import RiskBadge from "@/components/common/RiskBadge";
import EmptyState from "@/components/common/EmptyState";
import { calcAge, formatDate, formatDateTime, relativeFromNow } from "@/lib/format";
import { useAuth } from "@/contexts/AuthContext";
import AnamneseCustomManager from "@/components/common/AnamneseCustomManager";
import { ScaleField, RadioField, ChipsField, YesNoDetailField, OdontogramaField } from "@/components/common/AnamneseFields";

export default function PatientDetail() {
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const { clinic, clinicId } = useAuth();
  const myRole = clinic?.my_role || "professional";

  const availableTabs = [
    { id: "overview",    label: "Visão geral",  icon: FileText },
    { id: "anamnese",    label: "Anamnese",     icon: NotebookPen },
    { id: "evolucoes",   label: "Evoluções",    icon: Clock },
    { id: "prescricoes", label: "Prescrições",  icon: Pill },
    { id: "documentos",  label: "Documentos",   icon: Files },
  ].filter(tab => {
    if (myRole === 'receptionist') {
      return !['anamnese', 'evolucoes'].includes(tab.id);
    }
    return true;
  });

  const initialTab = availableTabs.find(t => t.id === searchParams.get("tab"))?.id || "overview";

  const [patient, setPatient] = useState(null);
  const [tab, setTab] = useState(initialTab);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try { setPatient(await getPatient(id, clinicId)); }
    catch { setPatient(null); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [id, clinicId]);
  
  useEffect(() => {
    const t = searchParams.get("tab");
    if (t && availableTabs.some(tabObj => tabObj.id === t)) {
      setTab(t);
    }
  }, [searchParams]);

  if (loading) return <div className="p-10 text-center text-sm text-[hsl(var(--text-tertiary))]">Carregando prontuário...</div>;
  if (!patient) {
    return (
      <EmptyState title="Prontuário não disponível"
                  description="O paciente pode ter sido removido ou o vínculo não está ativo."
                  action={<Link to="/app/pacientes" className="ds-btn-primary">Voltar para pacientes</Link>} />
    );
  }

  const age = calcAge(patient.birth_date);

  return (
    <div className="ds-fade-up">
      {/* Mobile Back Header */}
      <div className="md:hidden flex items-center gap-2 mb-4 -mt-2">
        <Link to="/app/pacientes" className="w-10 h-10 flex items-center justify-center text-[hsl(var(--text-tertiary))] active:bg-[hsl(var(--bg-hover))] rounded-full">
          <ArrowLeft className="w-5 h-5" />
        </Link>
        <span className="text-base font-bold text-[hsl(var(--text-primary))]">Prontuário</span>
      </div>

      {/* Desktop Breadcrumb */}
      <Link to="/app/pacientes" data-testid="back-to-patients"
            className="hidden md:inline-flex items-center gap-1 text-sm text-[hsl(var(--text-tertiary))] hover:text-[hsl(var(--text-primary))] mb-4">
        <ArrowLeft className="w-4 h-4" /> Pacientes
      </Link>

      <div className="ds-clinical-card mb-6 p-5 md:p-8">
        {patient.status === "pendente" && (
          <div className="mb-6 p-4 bg-[hsl(var(--warning-bg))] border border-[hsl(var(--warning-border))] rounded-xl flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3 text-[hsl(var(--warning-text))]">
              <Mail className="w-5 h-5" />
              <div>
                <div className="font-semibold text-sm">Solicitação de vínculo pendente</div>
                <div className="text-xs opacity-80">Este paciente deseja se vincular ao seu perfil profissional.</div>
              </div>
            </div>
            <div className="flex gap-2 w-full md:w-auto">
              <button onClick={async () => {
                if (window.confirm("Deseja recusar esta solicitação?")) {
                  try {
                    await setLinkStatus(patient.link_id, "blocked");
                    load();
                  } catch (e) {
                    console.error(e);
                    alert("Erro ao recusar: " + (e.message || "Erro desconhecido"));
                  }
                }
              }} className="ds-btn-secondary flex-1 md:flex-none border-[hsl(var(--danger-border))] text-[hsl(var(--danger-text))] hover:bg-[hsl(var(--danger-bg))]">
                Recusar
              </button>
              <button onClick={async () => {
                try {
                  if (!patient.link_id) throw new Error("ID de vínculo não encontrado.");
                  await setLinkStatus(patient.link_id, "active");
                  load();
                } catch (e) {
                  console.error(e);
                  alert("Erro ao aceitar: " + (e.message || "Erro desconhecido"));
                }
              }} className="ds-btn-primary flex-1 md:flex-none">
                Aceitar solicitação
              </button>
            </div>
          </div>
        )}

        <div className="flex flex-col md:flex-row md:items-center gap-6">
          <Avatar name={patient.full_name} size={64} className="ring-4 ring-[hsl(var(--bg-base))] shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-2 mb-1.5">
              <h1 className="text-xl md:text-3xl font-bold tracking-tight" style={{fontFamily:"'Outfit',sans-serif"}}>
                {patient.full_name}
              </h1>
              <PatientStatusBadge status={patient.status} />
            </div>
            
            <div className="text-sm text-[hsl(var(--text-secondary))] flex flex-wrap items-center gap-x-4 gap-y-1.5">
              {age !== null && <span className="flex items-center gap-1.5"><IdCard className="w-3.5 h-3.5 opacity-60" />{age} anos</span>}
              {patient.phone && <span className="flex items-center gap-1.5"><Phone className="w-3.5 h-3.5 opacity-60" />{patient.phone}</span>}
              {patient.email && <span className="hidden sm:inline-flex items-center gap-1.5"><Mail className="w-3.5 h-3.5 opacity-60" />{patient.email}</span>}
            </div>

            {(patient.tags?.length || patient.risk_flags?.length) ? (
              <div className="mt-4 flex flex-wrap gap-1.5">
                {(patient.risk_flags || []).map(f => <RiskBadge key={f} flag={f} />)}
                {(patient.tags || []).map(t => <span key={t} className="ds-badge ds-badge-brand">{t}</span>)}
              </div>
            ) : null}
          </div>
        </div>
      </div>

      <nav className="flex overflow-x-auto whitespace-nowrap no-scrollbar border-b border-[hsl(var(--border-divider))] mb-6 -mx-4 px-4 md:mx-0 md:px-0 md:overflow-x-visible md:whitespace-normal gap-2 md:gap-0 bg-transparent">
        {availableTabs.map((t) => (
          <button
            key={t.id}
            onClick={() => { setTab(t.id); setSearchParams({ tab: t.id }); }}
            className={`flex items-center justify-center gap-2 px-4 py-2.5 h-11 text-xs font-bold uppercase tracking-wider transition-all rounded-lg md:rounded-none border-b-2 shrink-0 ${
              tab === t.id 
                ? "bg-[hsl(var(--brand))]/10 text-[hsl(var(--brand))] border-[hsl(var(--brand))] md:bg-transparent md:text-[hsl(var(--brand))] md:border-b-[hsl(var(--brand))]" 
                : "border-transparent text-[hsl(var(--text-tertiary))] hover:text-[hsl(var(--text-primary))] md:border-transparent"
            }`}
          >
            <t.icon className="w-4 h-4 shrink-0" />
            <span>{t.label}</span>
          </button>
        ))}
      </nav>

      <div data-testid={`tab-content-${tab}`}>
        {tab === "overview" && <OverviewTab patient={patient} onUpdated={setPatient} myRole={myRole} clinicId={clinicId} />}
        {tab === "anamnese" && myRole !== 'receptionist' && <AnamneseTab patientId={patient.id} clinicId={clinicId} />}
        {tab === "evolucoes" && myRole !== 'receptionist' && <EvolutionsTab patient={patient} clinicId={clinicId} initialCreate={searchParams.get("new") === "true"} />}
        {tab === "prescricoes" && <PlaceholderTab title="Prescrições" description="Em breve." icon={Pill} />}
        {tab === "documentos" && <PlaceholderTab title="Documentos" description="Em breve." icon={Files} />}
      </div>
    </div>
  );
}

/* Overview */
function OverviewTab({ patient, onUpdated, myRole, clinicId }) {
  const { profile } = useAuth();
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ ...patient, tags: (patient.tags || []).join(", "), risk_flags: (patient.risk_flags || []).join(",") });
  const [saving, setSaving] = useState(false);
  const [profUsesCredits, setProfUsesCredits] = useState(true);

  useEffect(() => {
    // Fetch whether the current professional uses the credit system
    if (profile?.id) {
      getProfessionalUsesCredits(profile.id).then(setProfUsesCredits);
    }
  }, [profile?.id]);

  const save = async () => {
    setSaving(true);
    try {
      const updates = {
        full_name: form.full_name,
        email: form.email,
        phone: form.phone,
        birth_date: form.birth_date || null,
        gender: form.gender || null,
        document: form.document || null,
        address: form.address || null,
        notes: form.notes || null,
        tags: form.tags ? form.tags.split(",").map(s => s.trim()).filter(Boolean) : [],
        risk_flags: form.risk_flags ? form.risk_flags.split(",").map(s => s.trim()).filter(Boolean) : [],
      };
      await updatePatientProfile(patient.id, updates);
      const fresh = { ...patient, ...updates };
      onUpdated(fresh);
      setEditing(false);
    } finally { setSaving(false); }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
      <div className="lg:col-span-2 space-y-5">
        {!editing ? (
          <div className="grid grid-cols-2 md:grid-cols-2 gap-3 md:gap-4">
            <div className="ds-card p-3 md:p-5">
              <div className="flex items-center justify-between mb-3 md:mb-4">
                <div className="ds-overline">Identidade</div>
                <button data-testid="overview-edit" onClick={() => { setForm({ ...patient, tags: (patient.tags || []).join(", "), risk_flags: (patient.risk_flags || []).join(",") }); setEditing(true); }} className="p-1 hover:bg-[hsl(var(--bg-muted))] rounded-full transition-colors"><Plus className="w-4 h-4 rotate-45 text-[hsl(var(--text-tertiary))]" /></button>
              </div>
              <dl className="space-y-2 md:space-y-3 text-[11px] md:text-sm">
                <Info label="Nome" value={patient.full_name} />
                <Info label="Nascimento" value={patient.birth_date ? formatDate(patient.birth_date) : "—"} />
                <Info label="Gênero" value={patient.gender ? patient.gender.replace("_", " ") : "—"} />
                <Info label="CPF" value={patient.document || "—"} />
              </dl>
            </div>
            <div className="ds-card p-3 md:p-5">
              <div className="ds-overline mb-3 md:mb-4">Contato</div>
              <dl className="space-y-2 md:space-y-3 text-[11px] md:text-sm">
                <Info label="Status" value={<PatientStatusBadge status={patient.status} />} />
                <Info label="E-mail" value={patient.email || "—"} />
                <Info label="Telefone" value={patient.phone || "—"} />
              </dl>
            </div>
            {myRole !== 'receptionist' && (
              <div className="ds-card p-5 md:col-span-2">
                <div className="ds-overline mb-4">Observações Clínicas</div>
                <dl className="text-sm">
                  <Info label="Notas" value={patient.notes || "—"} full />
                </dl>
              </div>
            )}
          </div>
        ) : (
          <div className="ds-card p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold tracking-tight" style={{fontFamily:"'Outfit',sans-serif"}}>Editar informações</h3>
              <div className="flex gap-2">
                <button onClick={() => setEditing(false)} className="ds-btn-ghost">Cancelar</button>
                <button data-testid="overview-save" onClick={save} disabled={saving} className="ds-btn-primary">
                  <Save className="w-4 h-4" />{saving ? "Salvando..." : "Salvar"}
                </button>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField label="Nome" value={form.full_name} onChange={(v) => setForm({...form, full_name: v})} />
              <FormField label="E-mail" type="email" value={form.email} onChange={(v) => setForm({...form, email: v})} />
              <FormField label="Telefone" value={form.phone} onChange={(v) => setForm({...form, phone: v})} />
              <FormField label="Nascimento" type="date" value={form.birth_date} onChange={(v) => setForm({...form, birth_date: v})} />
              <FormField label="Gênero" select value={form.gender} onChange={(v) => setForm({...form, gender: v})}
                         options={[["",""],["feminino","Feminino"],["masculino","Masculino"],["nao_binario","Não-binário"],["outro","Outro"]]} />
              <FormField label="Documento" value={form.document} onChange={(v) => setForm({...form, document: v})} />
              <FormField label="Endereço" value={form.address} onChange={(v) => setForm({...form, address: v})} />
              {myRole !== 'receptionist' && (
                <>
                  <FormField label="Tags clínicas (vírgula)" value={form.tags} onChange={(v) => setForm({...form, tags: v})} />
                  <FormField label="Sinalizadores (vírgula)" value={form.risk_flags} onChange={(v) => setForm({...form, risk_flags: v})} full />
                  <FormField label="Observações" textarea value={form.notes} onChange={(v) => setForm({...form, notes: v})} full />
                </>
              )}
            </div>
          </div>
        )}
      </div>
      <div className="space-y-5">
        <div className="ds-card p-6">
          <div className="ds-overline">Resumo clínico</div>
          <h3 className="text-lg font-semibold tracking-tight mt-1" style={{fontFamily:"'Outfit',sans-serif"}}>Indicadores rápidos</h3>
          <ul className="mt-4 space-y-3 text-sm">
            <SummaryRow label="Cadastrado em" value={formatDate(patient.created_at)} />
            <SummaryRow label="Última atualização" value={relativeFromNow(patient.updated_at)} />
            <SummaryRow label="Tags" value={(patient.tags || []).join(", ") || "—"} />
            <SummaryRow label="Sinalizadores" value={(patient.risk_flags || []).length || 0} />
          </ul>
        </div>
        {myRole !== 'receptionist' && profUsesCredits && (
          <CreditCard patientId={patient.id} clinicId={clinicId} />
        )}
      </div>
    </div>
  );
}

function CreditCard({ patientId, clinicId }) {
  const [balance, setBalance] = useState(0);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [amount, setAmount] = useState(1);
  const [busy, setBusy] = useState(false);
  const load = async () => {
    setLoading(true);
    try { setBalance(await getPatientBalance(patientId, { clinic_id: clinicId })); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [patientId, clinicId]);
  const handleAdd = async () => {
    setBusy(true);
    try {
      await addPatientCredits(patientId, amount, "ajuste_manual", null, clinicId);
      await load();
      setAdding(false);
    } catch (e) {
      console.error(e);
      alert("Erro ao adicionar créditos: " + (e.message || JSON.stringify(e)));
    } finally { setBusy(false); }
  };
  return (
    <div className="ds-card p-6 border-t-4 border-[hsl(var(--brand))]">
      <div className="ds-overline">Financeiro</div>
      <h3 className="text-lg font-semibold tracking-tight mt-1" style={{fontFamily:"'Outfit',sans-serif"}}>Créditos de Sessão</h3>
      <div className="mt-4 p-4 bg-[hsl(var(--bg-base))] rounded-xl text-center">
        <div className="text-xs text-[hsl(var(--text-tertiary))] uppercase font-bold tracking-wider">Saldo Atual</div>
        <div className="text-4xl font-bold text-[hsl(var(--brand))] mt-1">{loading ? "..." : balance}</div>
        <div className="text-xs text-[hsl(var(--text-tertiary))] mt-1">sessões disponíveis</div>
      </div>
      {!adding ? (
        <button onClick={() => setAdding(true)} className="ds-btn-secondary w-full mt-4">
          <Plus className="w-4 h-4" /> Adicionar créditos
        </button>
      ) : (
        <div className="mt-4 space-y-3 p-3 border border-[hsl(var(--border-default))] rounded-lg ds-fade-up">
          <div className="text-xs font-medium">Quantidade de créditos:</div>
          <div className="flex items-center gap-2">
            <button onClick={() => setAmount(Math.max(1, amount - 1))} className="ds-btn-secondary px-3">-</button>
            <input type="number" className="ds-input text-center" value={amount} onChange={e => setAmount(Number(e.target.value))} />
            <button onClick={() => setAmount(amount + 1)} className="ds-btn-secondary px-3">+</button>
          </div>
          <div className="flex gap-2">
            <button onClick={() => setAdding(false)} className="ds-btn-ghost flex-1 text-xs">Cancelar</button>
            <button onClick={handleAdd} disabled={busy} className="ds-btn-primary flex-1 text-xs">
              {busy ? "Salvando..." : "Confirmar"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function Info({ label, value, full }) {
  return (
    <div className={full ? "md:col-span-2" : ""}>
      <div className="ds-overline mb-1">{label}</div>
      <div className="text-[hsl(var(--text-primary))]">{value || "—"}</div>
    </div>
  );
}
function SummaryRow({ label, value }) {
  return (
    <li className="flex items-center justify-between gap-3">
      <span className="text-[hsl(var(--text-tertiary))]">{label}</span>
      <span className="text-[hsl(var(--text-primary))] font-medium text-right">{value}</span>
    </li>
  );
}
function FormField({ label, value, onChange, type = "text", select, options = [], textarea, full }) {
  return (
    <label className={`block ${full ? "md:col-span-2" : ""}`}>
      <span className="text-xs font-medium text-[hsl(var(--text-secondary))] mb-1.5 block">{label}</span>
      {textarea ? (
        <textarea className="ds-textarea" value={value || ""} onChange={(e) => onChange(e.target.value)} />
      ) : select ? (
        <select className="ds-input" value={value || ""} onChange={(e) => onChange(e.target.value)}>
          {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
      ) : (
        <input className="ds-input" type={type} value={value || ""} onChange={(e) => onChange(e.target.value)} />
      )}
    </label>
  );
}

function ClinicalSection({ icon: Icon, title, children, color = "brand", defaultOpen = true }) {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const colors = {
    brand: "bg-[hsl(var(--brand-light))]/20 border-[hsl(var(--brand-light))]/50 text-[hsl(var(--brand))]",
    danger: "bg-[hsl(var(--danger-bg))] border-[hsl(var(--danger-border))] text-[hsl(var(--danger-text))]",
    info: "bg-[hsl(var(--info-bg))] border-[hsl(var(--info-border))] text-[hsl(var(--info-text))]",
    success: "bg-[hsl(var(--success-bg))] border-[hsl(var(--success-border))] text-[hsl(var(--success-text))]",
    warning: "bg-[hsl(var(--warning-bg))] border-[hsl(var(--warning-border))] text-[hsl(var(--warning-text))]",
    violet: "bg-violet-50 border-violet-100 text-violet-700",
  };
  return (
    <div className={`ds-card p-0 overflow-hidden ds-fade-up border-2 ${colors[color].split(" ")[1]}`}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={`w-full text-left px-6 py-4 border-b ${colors[color].split(" ")[1]} ${colors[color].split(" ")[0]} flex items-center justify-between transition-all hover:brightness-95 select-none`}
      >
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-white shadow-sm shrink-0">
            <Icon className="w-5 h-5" />
          </div>
          <h4 className="text-sm font-bold uppercase tracking-wider">{title}</h4>
        </div>
        <ChevronRight className={`w-5 h-5 transition-transform duration-200 ${isOpen ? "rotate-90 text-[hsl(var(--brand))]" : "text-gray-400"}`} />
      </button>
      {isOpen && (
        <div className="p-4 md:p-6 grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4 md:gap-y-6 animate-in fade-in duration-300">
          {children}
        </div>
      )}
    </div>
  );
}

function AnamneseField({ label, hint, value, onChange, full, rows = 3 }) {
  return (
    <label className={`block group min-w-0 ${full ? "md:col-span-2" : ""}`}>
      <span className="text-[10px] font-bold text-[hsl(var(--text-tertiary))] mb-1.5 block uppercase tracking-widest group-focus-within:text-[hsl(var(--brand))] transition-colors">
        {label}
      </span>
      <textarea
        className="ds-textarea bg-[hsl(var(--bg-base))]/50 focus:bg-white transition-all resize-none w-full"
        style={{ minHeight: rows === 1 ? '44px' : `${rows * 32}px` }}
        placeholder={hint}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
      />
      {hint && (
        <span className="mt-1.5 text-[10px] text-[hsl(var(--text-tertiary))] font-medium italic block opacity-0 group-focus-within:opacity-100 transition-opacity">
          {hint}
        </span>
      )}
    </label>
  );
}

/* =====================================================================
   Anamnese — base comum + seções específicas por tipo
   Chaves prefixadas com 'extra.' → gravadas em extra_fields (JSONB)
   Chaves sem prefixo              → colunas diretas da tabela anamneses
   ===================================================================== */
const COMMON_SECTIONS = [
  {
    icon: Stethoscope, title: "1. Queixa Principal (QP)", color: "brand",
    fields: [
      { key: "chief_complaint", label: "Queixa principal", hint: 'Preferencialmente com as palavras do paciente. Ex: "Dor de cabeça há 2 semanas".', full: true, rows: 2 },
    ]
  },
  {
    icon: History, title: "2. História da Doença Atual (HDA)", color: "brand",
    fields: [
      { key: "extra.hda_inicio",     label: "Quando começou?",         hint: "Data aproximada ou evento desencadeador.",           type: "text" },
      { key: "extra.hda_evolucao",   label: "Como evoluiu?",           hint: "Piora, melhora, flutuação, progressão.",             type: "text" },
      { key: "extra.hda_localizacao",label: "Localização / Irradiação",hint: "Onde é? Irradia para algum local?",                 type: "text" },
      { key: "extra.hda_intensidade",label: "Intensidade do sintoma (0–10)", type: "scale" },
      { key: "extra.hda_duracao",    label: "Duração / Frequência",    hint: "Constante? Intermitente? Quantas vezes ao dia/semana?", type: "text" },
      { key: "extra.hda_sintomas",   label: "Sintomas Associados",     hint: "Outros sintomas que acompanham a queixa principal.", full: true },
      { key: "extra.hda_melhora",    label: "Fatores de Melhora",      hint: "O que alivia os sintomas?",                        type: "text" },
      { key: "extra.hda_piora",      label: "Fatores de Piora",        hint: "O que piora os sintomas?",                         type: "text" },
      { key: "extra.hda_tratamentos",label: "Tratamentos Já Realizados",hint: "Medicações, terapias prévias e resposta obtida.",  full: true },
      { key: "extra.hda_impacto",    label: "Impacto na Rotina",       hint: "Como o problema afeta o dia a dia e as atividades?", full: true },
    ]
  },
  {
    icon: HeartPulse, title: "3. Antecedentes Pessoais", color: "danger",
    fields: [
      { key: "extra.ant_doencas",   label: "Doenças Prévias",          hint: "HAS, DM, cardiopatias, transtornos, outras.",      full: true },
      { key: "extra.ant_cirurgias", label: "Cirurgias / Internações",  hint: "Quando e por qual motivo.",                       full: true },
      { key: "allergies",           label: "Alergias",                  hint: "Medicamentos, alimentos, látex, anestésicos.",     full: true },
      { key: "medications",         label: "Medicamentos em Uso",       hint: "Nome, dose e horário de todos os medicamentos.",   full: true },
    ]
  },
  {
    icon: Users, title: "4. Antecedentes Familiares", color: "info",
    fields: [
      { key: "family_history", label: "Histórico Familiar Relevante", hint: "HAS, DM, cardiopatias, AVC, câncer, transtornos mentais, doenças hereditárias.", full: true },
    ]
  },
];

const TYPE_EXTRA_SECTIONS = {
  psicologo: [
    {
      icon: Activity, title: "5. Hábitos e Estilo de Vida", color: "success",
      fields: [
        { key: "extra.hab_sono",      label: "Sono",                hint: "Qualidade, duração, dificuldade para dormir/acordar.",    full: true },
        { key: "extra.hab_alimentacao",label:"Alimentação / Hidratação",hint:"Restrições, transtornos alimentares, qualidade geral.",  full: true },
        { key: "extra.hab_atividade", label: "Atividade Física",   hint: "Tipo, frequência, intensidade.",                          type: "text" },
        { key: "extra.hab_tabagismo", label: "Tabagismo",           type: "yesno", detailLabel: "Tipo e quantidade/tempo" },
        { key: "extra.hab_alcool",    label: "Álcool / Substâncias",type: "yesno", detailLabel: "Frequência e quantidade" },
        { key: "extra.hab_estresse",  label: "Estresse / Fatores Psicossociais", hint: "Trabalho, relacionamentos, traumas recentes.", full: true },
      ]
    },
    {
      icon: History, title: "6. Histórico Terapêutico", color: "warning",
      fields: [
        { key: "previous_treatments", label: "Psicoterapias e Tratamentos Anteriores", hint: "Abordagens, tempo de tratamento, motivo de término.", full: true },
        { key: "extra.psi_internacoes", label: "Internações Psiquiátricas", type: "radio", options: ["Sim", "Não"] },
        { key: "extra.psi_risco", label: "Ideação Suicida / Autolesão (atual ou prévia)", type: "radio", options: ["Sem ideação", "Ideação passiva", "Planejamento", "Tentativa prévia"] },
      ]
    },
    {
      icon: Brain, title: "7. Exame do Estado Mental (EEM)", color: "info",
      fields: [
        { key: "extra.eem_aparencia", label: "Aparência e Atitude",       hint: "Higiene, vestimenta, contato visual, cooperação.", full: true },
        { key: "extra.eem_consciencia", label: "Consciência e Orientação", hint: "Vigilância, orientação no tempo e espaço.",        full: true },
        { key: "extra.eem_atencao",    label: "Atenção e Memória",        hint: "Foco, fixação, evocação de eventos.",              full: true },
        { key: "extra.eem_pensamento", label: "Pensamento e Linguagem",   hint: "Curso, forma e conteúdo; clareza, fluência.",      full: true },
        { key: "extra.eem_humor",      label: "Humor e Afeto",            hint: "Estado emocional predominante e modulação.",       full: true },
      ]
    },
    {
      icon: LifeBuoy, title: "8. Hipótese Diagnóstica e Plano", color: "brand",
      fields: [
        { key: "diagnosis_hypothesis", label: "Hipótese Diagnóstica (CID / DSM)", hint: "Impressão clínica e enquadramentos possíveis.", full: true },
        { key: "treatment_plan",       label: "Plano Terapêutico",               hint: "Objetivos, frequência de sessões e abordagem.", full: true },
        { key: "lifestyle",            label: "Observações Complementares",       hint: "Aspectos relevantes não cobertos acima.",       full: true },
      ]
    },
  ],
  medico: [
    {
      icon: Activity, title: "5. Hábitos e Estilo de Vida", color: "success",
      fields: [
        { key: "extra.hab_sono",       label: "Sono",                hint: "Qualidade e duração.",                                type: "text" },
        { key: "extra.hab_alimentacao",label: "Alimentação / Hidratação", hint: "Dieta especial, ingestão hídrica.",              type: "text" },
        { key: "extra.hab_atividade",  label: "Atividade Física",   hint: "Tipo e frequência.",                                  type: "text" },
        { key: "extra.hab_tabagismo",  label: "Tabagismo",           type: "yesno", detailLabel: "Maços/dia e tempo de uso" },
        { key: "extra.hab_alcool",     label: "Álcool",              type: "yesno", detailLabel: "Frequência e quantidade" },
        { key: "extra.hab_estresse",   label: "Estresse / Fatores Psicossociais", hint: "Contexto social e emocional.",           full: true },
      ]
    },
    {
      icon: HeartPulse, title: "6. Exame Físico (EF)", color: "danger",
      fields: [
        { key: "extra.ef_geral",       label: "Estado Geral / Sinais Vitais", hint: "PA, FC, FR, Temp, SatO2, Peso, Altura, IMC.", full: true },
        { key: "extra.ef_cabeca",      label: "Cabeça e Pescoço",             hint: "Linfonodos, tireoide, oroscopia.",             full: true },
        { key: "extra.ef_torax",       label: "Tórax (Cardio/Respiratório)",  hint: "Ausculta, expansibilidade, ruídos.",           full: true },
        { key: "extra.ef_abdome",      label: "Abdome",                       hint: "RHA, massas, visceromegalias, dor.",           full: true },
        { key: "extra.ef_membros",     label: "Membros e Neurológico",        hint: "Edemas, pulsos, força, reflexos.",             full: true },
      ]
    },
    {
      icon: LifeBuoy, title: "7. Hipótese Diagnóstica e Conduta", color: "brand",
      fields: [
        { key: "diagnosis_hypothesis", label: "Hipótese Diagnóstica (HD)",     hint: "Diagnóstico principal e diferenciais (CID-10).",   full: true },
        { key: "treatment_plan",       label: "Conduta / Plano Terapêutico",    hint: "Medicações, exames solicitados, orientações.",     full: true },
        { key: "lifestyle",            label: "Observações Adicionais",          hint: "Outros aspectos relevantes.",                      full: true },
      ]
    },
  ],
  fisioterapeuta: [
    {
      icon: Bone, title: "5. Avaliação Funcional", color: "warning",
      fields: [
        { key: "extra.fisio_postural",    label: "Avaliação Postural",          hint: "Desvios posturais e compensações observadas.",      full: true },
        { key: "extra.fisio_adm",         label: "Amplitude de Movimento",      hint: "Limitações articulares identificadas.",             type: "text" },
        { key: "extra.fisio_testes",      label: "Testes Específicos Realizados",hint: "Ortolani, Spurling, Lasègue, etc.",               type: "text" },
        { key: "extra.hda_intensidade",   label: "Escala de Dor (EVA 0–10)",    type: "scale" },
        { key: "extra.fisio_padrao_dor",  label: "Padrão e Comportamento da Dor",hint: "Constante, mecânica, noturna, irradiação.",        full: true },
        { key: "extra.fisio_limitacoes",  label: "Limitações Funcionais",       hint: "Atividades que não consegue realizar por causa da condição.", full: true },
      ]
    },
    {
      icon: LifeBuoy, title: "6. Plano Fisioterapêutico", color: "brand",
      fields: [
        { key: "previous_treatments",      label: "Tratamentos Anteriores",         hint: "Cirurgias, órteses, fisioterapia prévia, medicamentos.", full: true },
        { key: "diagnosis_hypothesis",     label: "Diagnóstico Cinético-Funcional",  hint: "Hipótese diagnóstica fisioterapêutica.",               full: true },
        { key: "treatment_plan",           label: "Objetivos Terapêuticos",         hint: "Metas a curto e longo prazo acordadas com o paciente.",  full: true },
        { key: "lifestyle",                label: "Observações Adicionais",         hint: "Aspectos relevantes para o tratamento.",               full: true },
      ]
    },
  ],
  dentista: [
    {
      icon: Smile, title: "5. Histórico Odontológico", color: "warning",
      fields: [
        { key: "previous_treatments",      label: "Tratamentos Odontológicos Realizados", hint: "Extrações, implantes, ortodontia, próteses.", full: true },
        { key: "extra.dent_medo",          label: "Ansiedade / Medo ao Dentista",         type: "radio", options: ["Nenhum", "Leve", "Moderado", "Intenso"] },
        { key: "extra.dent_habitos",       label: "Hábitos Orais",                        type: "chips", options: ["Bruxismo","Onicofagia","Sucção digital","Morder objetos","Ronco","Apneia"] },
        { key: "extra.dent_higiene",       label: "Higiene Oral",                         hint: "Escovação (vezes/dia), fio dental, enxaguante.", type: "text" },
        { key: "extra.dent_ultimo",        label: "Última Consulta Odontológica",         hint: "Quando e por qual motivo.",                     type: "text" },
      ]
    },
    {
      icon: Activity, title: "6. Odontograma Interativo", color: "brand",
      fields: [
        { key: "extra.dent_odontograma", label: "Mapa Dentário e Diagnóstico Clínico", type: "odontogram", full: true }
      ]
    },
    {
      icon: LifeBuoy, title: "7. Diagnóstico e Plano", color: "brand",
      fields: [
        { key: "diagnosis_hypothesis", label: "Diagnóstico / Achados Clínicos", hint: "Diagnóstico clínico e radiográfico.",        full: true },
        { key: "treatment_plan",       label: "Plano de Tratamento",           hint: "Procedimentos planejados e sequência.",      full: true },
        { key: "lifestyle",            label: "Orientações e Observações",      hint: "Orientações de higiene, retornos e outros.", full: true },
      ]
    },
  ],
  outro: [
    {
      icon: Activity, title: "5. Hábitos e Estilo de Vida", color: "success",
      fields: [
        { key: "lifestyle", label: "Hábitos Relevantes", hint: "Sono, alimentação, atividade física, substâncias.", full: true },
      ]
    },
    {
      icon: LifeBuoy, title: "6. Diagnóstico e Plano", color: "brand",
      fields: [
        { key: "diagnosis_hypothesis", label: "Avaliação / Diagnóstico", hint: "Impressão clínica.",  full: true },
        { key: "treatment_plan",       label: "Plano de Tratamento",     hint: "Objetivos e conduta.", full: true },
      ]
    },
  ],
};

function getFieldValue(data, key) {
  if (key.startsWith("extra.")) return (data.extra_fields || {})[key.slice(6)] ?? "";
  return data[key] ?? "";
}
function setFieldValue(data, key, v) {
  if (key.startsWith("extra.")) {
    return { ...data, extra_fields: { ...(data.extra_fields || {}), [key.slice(6)]: v } };
  }
  return { ...data, [key]: v };
}

function DynField({ field, data, onChange }) {
  const value = getFieldValue(data, field.key);
  const set   = v => onChange(setFieldValue(data, field.key, v));
  if (field.type === "scale")  return <ScaleField  label={field.label} hint={field.hint} value={value} onChange={set} />;
  if (field.type === "radio")  return <RadioField  label={field.label} hint={field.hint} value={value} onChange={set} options={field.options} />;
  if (field.type === "chips")  return <ChipsField  label={field.label} hint={field.hint} value={value} onChange={set} options={field.options} />;
  if (field.type === "yesno")  return <YesNoDetailField label={field.label} hint={field.hint} value={value} onChange={set} detailLabel={field.detailLabel} />;
  if (field.type === "text")   return <AnamneseField label={field.label} hint={field.hint} value={value} onChange={set} rows={1} />;
  if (field.type === "odontogram") return <OdontogramaField label={field.label} hint={field.hint} value={value} onChange={set} />;
  return <AnamneseField label={field.label} hint={field.hint} value={value} onChange={set} full={field.full} />;
}


function AnamneseTab({ patientId, clinicId }) {
  const { profile } = useAuth();
  const [anamnesesList, setAnamnesesList] = useState([]);
  const [selectedProfId, setSelectedProfId] = useState(profile?.id);
  const [data, setData]           = useState(null);
  const [profType, setProfType]   = useState(null);
  const [templates, setTemplates] = useState([]);
  const [saving, setSaving]       = useState(false);
  const [savedAt, setSavedAt]     = useState(null);
  const [loadingMeta, setLoadingMeta] = useState(true);

  useEffect(() => {
    Promise.all([
      listAnamneses(patientId, clinicId),
      profile?.id ? getProfessionalProfile(profile.id) : Promise.resolve(null),
      listAnamneseTemplates(),
    ]).then(([anamList, prof, tmpl]) => {
      let fullList = [...anamList];
      const mine = fullList.find(a => a.professional_id === profile.id);
      
      if (!mine) {
        const emptyMine = { patient_id: patientId, professional_id: profile.id, clinic_id: clinicId };
        fullList = [...fullList, emptyMine];
      }
      
      setAnamnesesList(fullList);
      
      setProfType(prof?.professional_type || "outro");
      setTemplates(tmpl);
    }).finally(() => setLoadingMeta(false));
  }, [patientId, clinicId, profile?.id]);

  useEffect(() => {
    if (anamnesesList.length > 0) {
      const selected = anamnesesList.find(a => a.professional_id === selectedProfId);
      setData(selected || anamnesesList[0]);
    }
  }, [selectedProfId, anamnesesList]);

  if (loadingMeta || !data) {
    return <div className="ds-card p-8 text-sm text-[hsl(var(--text-tertiary))]">Carregando anamnese...</div>;
  }

  const typeSections  = TYPE_EXTRA_SECTIONS[profType] || TYPE_EXTRA_SECTIONS.outro;
  const allSections   = [...COMMON_SECTIONS, ...typeSections];

  const LABELS = { psicologo: "Anamnese Psicológica", medico: "Anamnese Médica", fisioterapeuta: "Anamnese Fisioterapêutica", dentista: "Anamnese Odontológica", outro: "Anamnese Geral" };
  const BADGES = { psicologo: "Psicólogo(a)", medico: "Médico(a)", fisioterapeuta: "Fisioterapeuta", dentista: "Dentista", outro: "Profissional de Saúde" };

  const isReadOnly = data.professional_id !== profile.id;

  const save = async () => {
    setSaving(true);
    try {
      const customExtras = {};
      templates.forEach(t => { customExtras[t.question_key] = (data.extra_fields || {})[t.question_key] || ""; });
      const merged_extra = { ...(data.extra_fields || {}), ...customExtras };
      const out = await upsertAnamnese(patientId, {
        chief_complaint: data.chief_complaint, history: data.history,
        family_history: data.family_history,   medications: data.medications,
        allergies: data.allergies,             lifestyle: data.lifestyle,
        previous_treatments: data.previous_treatments,
        diagnosis_hypothesis: data.diagnosis_hypothesis,
        treatment_plan: data.treatment_plan,
        extra_fields: merged_extra,
      }, clinicId);
      setData(out);
      setSavedAt(new Date());
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between ds-fade-up">
        <div className="min-w-0">
          <div className="ds-overline">Prontuário Permanente</div>
          <h3 className="text-xl md:text-2xl font-bold tracking-tight" style={{fontFamily:"'Outfit',sans-serif"}}>{LABELS[profType] || "Anamnese"}</h3>
          {profType && (
            <span className="inline-flex items-center gap-1 mt-1 text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full bg-[hsl(var(--brand-light))] text-[hsl(var(--brand))]">
              {BADGES[profType] || profType}
            </span>
          )}
        </div>
        <div className="flex flex-col items-start sm:items-end gap-3 shrink-0">
          {anamnesesList.length > 1 && (
            <select 
              className="ds-input text-sm h-9 py-0 w-full sm:w-auto"
              value={selectedProfId}
              onChange={(e) => setSelectedProfId(e.target.value)}
            >
              {anamnesesList.map(a => (
                <option key={a.professional_id} value={a.professional_id}>
                  {a.professional_id === profile.id ? "Minha Anamnese" : `Anamnese: ${a.professional?.full_name || "Outro profissional"}`}
                </option>
              ))}
            </select>
          )}
          {!isReadOnly && (
            <div className="flex items-center gap-3 flex-wrap">
              {savedAt && (
                <div className="flex items-center gap-1.5 text-xs text-[hsl(var(--success-text))] bg-[hsl(var(--success-bg))] px-3 py-1.5 rounded-full border border-[hsl(var(--success-border))]">
                  <span className="w-1.5 h-1.5 rounded-full bg-[hsl(var(--success-text))] animate-pulse" />
                  Alterações salvas
                </div>
              )}
              <button data-testid="anamnese-save" onClick={save} disabled={saving} className="ds-btn-primary px-4 md:px-6 h-10 md:h-11 gap-2 shadow-lg shadow-[hsl(var(--brand))]/10 text-sm">
                <Save className="w-4 h-4" /> {saving ? "Salvando..." : "Salvar anamnese"}
              </button>
            </div>
          )}
        </div>
      </div>

      <div className={`grid grid-cols-1 gap-6 ${isReadOnly ? 'opacity-80 pointer-events-none grayscale-[0.2]' : ''}`}>
        {allSections.map((section, si) => (
          <ClinicalSection key={si} icon={section.icon} title={section.title} color={section.color}>
            {section.fields.map(f => (
              <DynField key={f.key} field={f} data={data} onChange={setData} />
            ))}
          </ClinicalSection>
        ))}

        {templates.length > 0 && (
          <ClinicalSection icon={HelpCircle} title="Perguntas Personalizadas" color="violet">
            {templates.map(t => (
              <AnamneseField
                key={t.question_key}
                label={t.question_label}
                hint={t.question_hint || ""}
                value={(data.extra_fields || {})[t.question_key] || ""}
                onChange={v => setData({ ...data, extra_fields: { ...(data.extra_fields || {}), [t.question_key]: v } })}
                full
              />
            ))}
          </ClinicalSection>
        )}

        {!isReadOnly && <AnamneseCustomManager templates={templates} onUpdated={setTemplates} />}
      </div>
    </div>
  );
}

function EvolutionsTab({ patient, clinicId, initialCreate = false }) {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(initialCreate);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptySession());
  const [anamnese, setAnamnese] = useState(null);
  const [showAnamneseSummary, setShowAnamneseSummary] = useState(false);
  const { user } = useAuth();
  const currentUserId = user?.id;

  const lastSession = list.length > 0 ? list[0] : null;

  function emptySession() {
    const now = new Date();
    const local = new Date(now.getTime() - now.getTimezoneOffset() * 60000);
    return {
      session_date: local.toISOString().slice(0, 16),
      duration_min: 50,
      mood_scale: 5,
      subjective: "", objective: "", assessment: "", plan: "",
      techniques: "",
    };
  }

  const load = async () => {
    setLoading(true);
    try { 
      const [sessions, ana] = await Promise.all([
        listPatientSessions(patient.id),
        getAnamnese(patient.id, clinicId)
      ]);
      setList(sessions);
      setAnamnese(ana);
    }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [patient.id]);

  const submit = async () => {
    if (!form.subjective && !form.objective && !form.assessment && !form.plan) {
      alert("Preencha pelo menos um campo SOAP."); return;
    }
    const payload = {
      patient_id: patient.id,
      session_date: new Date(form.session_date).toISOString(),
      duration_min: Number(form.duration_min) || 50,
      mood_scale: Number(form.mood_scale) || null,
      subjective: form.subjective, objective: form.objective,
      assessment: form.assessment, plan: form.plan,
      techniques: form.techniques ? (Array.isArray(form.techniques) ? form.techniques : form.techniques.split(",").map(s => s.trim()).filter(Boolean)) : [],
    };
    
    try {
      if (editingId) {
        await updateSession(editingId, payload);
      } else {
        await createSession(payload, clinicId);
      }
      
      setCreating(false); 
      setEditingId(null);
      setForm(emptySession()); 
      load();
    } catch (error) {
      console.error("Erro ao salvar evolução:", error);
      alert(`Erro ao salvar evolução: ${error?.message || JSON.stringify(error)}`);
    }
  };

  const editSess = (s) => {
    setForm({
      session_date: new Date(new Date(s.session_date).getTime() - new Date().getTimezoneOffset() * 60000).toISOString().slice(0, 16),
      duration_min: s.duration_min,
      mood_scale: s.mood_scale,
      subjective: s.subjective,
      objective: s.objective,
      assessment: s.assessment,
      plan: s.plan,
      techniques: s.techniques?.join(", ") || "",
    });
    setEditingId(s.id);
    setCreating(true);
  };

  const removeSess = async (id) => {
    if (!window.confirm("Excluir esta evolução?")) return;
    await deleteSession(id); load();
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <div className="ds-overline">Linha do tempo</div>
          <h3 className="text-lg font-semibold tracking-tight" style={{fontFamily:"'Outfit',sans-serif"}}>Evoluções clínicas (modelo SOAP)</h3>
        </div>
        <button data-testid="new-session-btn" onClick={() => { 
          if (creating && !editingId) setCreating(false);
          else { setEditingId(null); setForm(emptySession()); setCreating(true); }
        }} className="ds-btn-primary h-11 px-6 shadow-lg shadow-[hsl(var(--brand))]/10">
          <Plus className="w-4.5 h-4.5" /> Nova evolução
        </button>
      </div>

      {anamnese && (
        <div className="ds-card p-0 overflow-hidden border-[hsl(var(--brand-light))] border-2">
          <button 
            onClick={() => setShowAnamneseSummary(!showAnamneseSummary)}
            className="w-full px-5 py-3 bg-[hsl(var(--brand-light))]/30 flex items-center justify-between hover:bg-[hsl(var(--brand-light))]/50 transition-colors"
          >
            <div className="flex items-center gap-2 text-[hsl(var(--brand))] font-bold text-xs uppercase tracking-widest">
              <Stethoscope className="w-4 h-4" /> Resumo da Anamnese e Queixa
            </div>
            <ChevronRight className={`w-4 h-4 text-[hsl(var(--brand))] transition-transform ${showAnamneseSummary ? 'rotate-90' : ''}`} />
          </button>
          {showAnamneseSummary && (
            <div className="p-5 grid grid-cols-1 md:grid-cols-3 gap-6 ds-fade-up">
              <div className="space-y-1">
                <div className="text-[10px] uppercase font-bold text-[hsl(var(--text-tertiary))] tracking-wider">Queixa Principal</div>
                <div className="text-sm text-[hsl(var(--text-primary))] line-clamp-3 italic">"{anamnese.chief_complaint || "Não informada"}"</div>
              </div>
              <div className="space-y-1">
                <div className="text-[10px] uppercase font-bold text-[hsl(var(--text-tertiary))] tracking-wider">Impressão Clínica / HD</div>
                <div className="text-sm text-[hsl(var(--text-primary))] line-clamp-3">{anamnese.diagnosis_hypothesis || "—"}</div>
              </div>
              <div className="space-y-1">
                <div className="text-[10px] uppercase font-bold text-[hsl(var(--text-tertiary))] tracking-wider">Conduta / Plano</div>
                <div className="text-sm text-[hsl(var(--brand))] font-medium line-clamp-3">{anamnese.treatment_plan || "—"}</div>
              </div>
            </div>
          )}
        </div>
      )}

      {creating && (
        <div className="ds-card p-0 overflow-hidden ds-fade-up border-2 border-[hsl(var(--brand))]/20" data-testid="new-session-form">
          <div className="px-6 py-4 border-b border-[hsl(var(--border-default))] bg-[hsl(var(--bg-base))]/50 flex items-center justify-between">
            <h4 className="text-sm font-bold uppercase tracking-wider text-[hsl(var(--brand))]">
              {editingId ? "Editar Evolução" : "Nova Evolução Clínica"}
            </h4>
            {!editingId && lastSession && (
              <div className="flex items-center gap-1.5 text-[10px] font-bold text-[hsl(var(--success-text))] bg-[hsl(var(--success-bg))] px-2 py-1 rounded border border-[hsl(var(--success-border))]">
                <History className="w-3 h-3" /> Efeito Cascata Ativo
              </div>
            )}
          </div>

          <div className="p-6">
            {!editingId && lastSession && (
              <div className="mb-6 p-4 bg-blue-50 border border-blue-100 rounded-xl flex gap-4 animate-in slide-in-from-top-2 duration-500">
                <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center shrink-0">
                  <Activity className="w-5 h-5 text-blue-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[10px] font-bold text-blue-600 uppercase tracking-widest mb-1">Na última sessão ({formatDate(lastSession.session_date)})</div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <div className="text-[9px] font-bold text-blue-400 uppercase">Avaliação Anterior</div>
                      <div className="text-xs text-blue-800 line-clamp-2 italic">"{lastSession.assessment || "Sem avaliação registrada"}"</div>
                    </div>
                    <div>
                      <div className="text-[9px] font-bold text-blue-400 uppercase">Plano a Seguir</div>
                      <div className="text-xs text-blue-900 font-semibold line-clamp-2">{lastSession.plan || "Sem plano definido"}</div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Guia SOAP de Referência Rápida */}
            <div className="mb-6 p-4 bg-[hsl(var(--brand-light))]/20 border border-[hsl(var(--brand-light))]/50 rounded-xl">
              <div className="flex items-center gap-2 mb-3">
                <Sparkles className="w-3.5 h-3.5 text-[hsl(var(--brand))]" />
                <span className="text-[10px] font-bold uppercase tracking-widest text-[hsl(var(--brand))]">Guia de Preenchimento SOAP</span>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <div className="text-[9px] font-bold text-[hsl(var(--brand))] uppercase mb-0.5">S · Subjetivo</div>
                  <div className="text-[10px] text-[hsl(var(--text-secondary))] leading-tight">O que o paciente relata: queixas, sentimentos e percepções.</div>
                </div>
                <div>
                  <div className="text-[9px] font-bold text-[hsl(var(--brand))] uppercase mb-0.5">O · Objetivo</div>
                  <div className="text-[10px] text-[hsl(var(--text-secondary))] leading-tight">O que você observa: sinais, exames, postura e comportamento.</div>
                </div>
                <div>
                  <div className="text-[9px] font-bold text-[hsl(var(--brand))] uppercase mb-0.5">A · Avaliação</div>
                  <div className="text-[10px] text-[hsl(var(--text-secondary))] leading-tight">Sua análise técnica: hipóteses, progressos e diagnóstico.</div>
                </div>
                <div>
                  <div className="text-[9px] font-bold text-[hsl(var(--brand))] uppercase mb-0.5">P · Plano</div>
                  <div className="text-[10px] text-[hsl(var(--text-secondary))] leading-tight">Próximos passos: tarefas, conduta e data do retorno.</div>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <FormField label="Data e hora" type="datetime-local" value={form.session_date} onChange={(v) => setForm({...form, session_date: v})} />
              <FormField label="Duração (min)" type="number" value={form.duration_min} onChange={(v) => setForm({...form, duration_min: v})} />
              <FormField label="Humor (1-10)" type="number" value={form.mood_scale} onChange={(v) => setForm({...form, mood_scale: v})} />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <SoapField data-testid="soap-s" label="S — Subjetivo" hint="Relato do paciente." value={form.subjective} onChange={(v) => setForm({...form, subjective: v})} />
              <SoapField data-testid="soap-o" label="O — Objetivo" hint="Observações da sessão." value={form.objective} onChange={(v) => setForm({...form, objective: v})} />
              <SoapField data-testid="soap-a" label="A — Avaliação" hint="Análise clínica e hipóteses." value={form.assessment} onChange={(v) => setForm({...form, assessment: v})} />
              <SoapField data-testid="soap-p" label="P — Plano" hint="Próximos passos, técnicas." value={form.plan} onChange={(v) => setForm({...form, plan: v})} />
            </div>
            <div className="mt-4">
              <FormField label="Técnicas (vírgula)" value={form.techniques} onChange={(v) => setForm({...form, techniques: v})} />
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <button onClick={() => { setCreating(false); setEditingId(null); }} className="ds-btn-secondary">Cancelar</button>
              <button data-testid="session-submit" onClick={submit} className="ds-btn-primary">
                <Save className="w-4 h-4" /> {editingId ? "Salvar alterações" : "Salvar evolução"}
              </button>
            </div>
          </div>
        </div>
      )}

      {loading ? (
        <div className="ds-card p-10 text-center text-sm text-[hsl(var(--text-tertiary))]">Carregando evoluções...</div>
      ) : list.length === 0 ? (
        <EmptyState icon={NotebookPen} title="Nenhuma evolução registrada"
                    description="Cadastre a primeira sessão clínica para iniciar a linha do tempo."
                    action={<button onClick={() => setCreating(true)} className="ds-btn-primary"><Plus className="w-4 h-4" /> Nova evolução</button>} />
      ) : (
        <ol className="relative ml-4 border-l-2 border-[hsl(var(--border-default))] space-y-5" data-testid="sessions-timeline">
          {list.map((s, i) => (
            <li key={s.id} className="pl-6 relative ds-fade-up" style={{animationDelay: `${i * 60}ms`}}>
              <span className="absolute -left-[9px] top-2 w-4 h-4 rounded-full bg-white border-2 border-[hsl(var(--brand))]" />
              <div className="ds-card p-5">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <CalendarDays className="w-4 h-4 text-[hsl(var(--brand))]" />
                    <span className="text-sm font-medium">{formatDateTime(s.session_date)}</span>
                    <span className="text-xs text-[hsl(var(--text-tertiary))]">· {s.duration_min} min</span>
                    {s.mood_scale && <span className="ds-badge ds-badge-info ml-1">Humor {s.mood_scale}/10</span>}
                  </div>
                  <div className="flex items-center gap-2">
                    {s.professional_id === currentUserId ? (
                      <>
                        <button onClick={() => editSess(s)} className="text-[hsl(var(--text-tertiary))] hover:text-[hsl(var(--brand))]" aria-label="Editar">
                          <NotebookPen className="w-4 h-4" />
                        </button>
                        <button onClick={() => removeSess(s.id)} className="text-[hsl(var(--text-tertiary))] hover:text-[hsl(var(--danger-text))]" aria-label="Excluir">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </>
                    ) : (
                      <span className="text-[10px] font-bold uppercase text-[hsl(var(--text-tertiary))] tracking-widest bg-[hsl(var(--bg-base))] px-2 py-1 rounded border border-[hsl(var(--border-default))]">
                        {s.professional?.full_name?.split(' ')[0] || "Outro profissional"}
                        {s.professional?.professional_profile?.[0]?.crp ? ` · CRP ${s.professional.professional_profile[0].crp}` : ""}
                      </span>
                    )}
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3">
                  <SoapView label="S · Subjetivo" value={s.subjective} />
                  <SoapView label="O · Objetivo" value={s.objective} />
                  <SoapView label="A · Avaliação" value={s.assessment} />
                  <SoapView label="P · Plano" value={s.plan} />
                </div>
                {s.techniques?.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-1.5">
                    {s.techniques.map(t => <span key={t} className="ds-badge ds-badge-brand">{t}</span>)}
                  </div>
                )}
              </div>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}

function SoapField({ label, hint, value, onChange, ...rest }) {
  return (
    <label className="block group">
      <span className="text-xs font-bold text-[hsl(var(--brand))] mb-1.5 block uppercase tracking-tight">{label}</span>
      <textarea 
        {...rest} 
        className="ds-textarea min-h-[140px] bg-[hsl(var(--bg-base))]/30 focus:bg-white transition-all" 
        placeholder={hint}
        value={value || ""} 
        onChange={(e) => onChange(e.target.value)} 
      />
      <span className="mt-1.5 text-[10px] text-[hsl(var(--text-secondary))] font-medium italic block opacity-80 group-focus-within:opacity-100 transition-opacity">
        {hint}
      </span>
    </label>
  );
}
function SoapView({ label, value }) {
  return (
    <div className="border-l-2 border-[hsl(var(--brand-light))] pl-3">
      <div className="text-[11px] font-semibold tracking-wide text-[hsl(var(--brand))] uppercase mb-1">{label}</div>
      <div className="text-sm text-[hsl(var(--text-primary))] whitespace-pre-wrap leading-relaxed">{value || "—"}</div>
    </div>
  );
}
function PlaceholderTab({ title, description, icon }) {
  return <EmptyState icon={icon} title={title} description={description} />;
}
